import type { ActivityManifest } from "@nmblr-platform-flow/activities";
import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import { useEffect, useState } from "react";
import { api } from "../lib/api-client.js";

export type { JsonSchemaNode, ActivityManifest };

interface UseActivityManifestState {
	manifest: ActivityManifest | null;
	loading: boolean;
	error: string | null;
}

// In-memory cache — static for built-in activities; acceptable until plugin manifests are added
const cache = new Map<string, ActivityManifest>();

export function useActivityManifest(stepType: string | undefined): UseActivityManifestState {
	const [state, setState] = useState<UseActivityManifestState>({
		manifest: stepType ? (cache.get(stepType) ?? null) : null,
		loading: stepType !== undefined && !cache.has(stepType ?? ""),
		error: null,
	});

	useEffect(() => {
		if (!stepType) {
			setState({ manifest: null, loading: false, error: null });
			return;
		}

		const cached = cache.get(stepType);
		if (cached) {
			setState({ manifest: cached, loading: false, error: null });
			return;
		}

		setState((s) => ({ ...s, loading: true, error: null }));

		api
			.get<ActivityManifest>(`/activities/${stepType}/manifest`)
			.then((manifest) => {
				cache.set(stepType, manifest);
				setState({ manifest, loading: false, error: null });
			})
			.catch((err) => {
				setState({
					manifest: null,
					loading: false,
					error: err instanceof Error ? err.message : "Failed to load manifest",
				});
			});
	}, [stepType]);

	return state;
}

export function getManifestCache(): ReadonlyMap<string, ActivityManifest> {
	return cache;
}
